import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Users, Building, LogIn } from 'lucide-react';
import { useQuery } from '@apollo/client';
import AdminLayout from '../../components/admin/AdminLayout';
import { GET_AGENCY_PACKAGES } from '../../graphql/admin/queries/package';
import { GET_CLIENTS } from '../../graphql/admin/queries/client';
import { GET_BUSINESSES } from '../../graphql/admin/queries/business';
import { useAuth } from '../../lib/auth';
import LoadingSpinner from '../../components/LoadingSpinner';
import { getImageUrl } from '../../utils/imageUrl';

// Définissez une interface pour votre package
interface Package {
  id: number | string;
  title: string;
  category: string;
  image?: string;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [someState, setSomeState] = useState(null);
  
  // Utilisez l'objet user directement de useAuth()
  const agencyName = user?.agencyName || '';
  
  
  // Utiliser une valeur par défaut plus explicite pour debug
  const safeAgencyName = agencyName || 'NO_AGENCY_NAME_PROVIDED';
  
  // Fonction pour simuler une connexion
  const handleFakeLogin = async () => {
    await login('admin@example.com', 'password');
    window.location.reload();
  };
  
  // Faire TOUTES les requêtes GraphQL ici, avant les conditions
  const { data: packagesData, loading: packagesLoading, error: packagesError } = useQuery(GET_AGENCY_PACKAGES, {
    variables: { agencyName },
    skip: !agencyName,
    fetchPolicy: 'network-only'
  });
  
  const { data: clientsData, loading: clientsLoading, error: clientsError } = useQuery(GET_CLIENTS, {
    variables: { agencyName },
    skip: !agencyName || agencyName === '',
    fetchPolicy: 'network-only'
  });
  
  const { data: businessesData, loading: businessesLoading, error: businessesError } = useQuery(GET_BUSINESSES, {
    variables: { agencyName },
    skip: !agencyName,
    fetchPolicy: 'network-only'
  });
  
  // Définir useEffect avant les conditions
  useEffect(() => {
    
    
    if (packagesError) console.error('Packages error:', packagesError);
    if (clientsError) console.error('Clients error:', clientsError);
    if (businessesError) console.error('Businesses error:', businessesError);
  }, [packagesData, clientsData, businessesData, packagesError, clientsError, businessesError]);
  
  // Si l'utilisateur n'est pas connecté, afficher un écran de connexion temporaire
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-center h-96">
          <h1 className="text-2xl font-bold mb-4">Vous n'êtes pas connecté</h1>
          <p className="text-gray-600 mb-6">Connectez-vous pour accéder au tableau de bord</p>
          <div className="flex gap-4">
            <button
              onClick={handleFakeLogin}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <LogIn className="w-4 h-4" />
              Connexion temporaire
            </button>
            <Link
              to="/login"
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Aller à la page de connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  // Déplacer ces calculs après tous les hooks mais avant les conditions de rendu
  const packagesCount = packagesData?.agencyPackages?.length || 0;
  const clientsCount = clientsData?.clients?.length || 0;
  const businessesCount = businessesData?.businesses?.length || 0;
  
  const isLoading = packagesLoading || clientsLoading || businessesLoading;
  
  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Tableau de bord</h1>
        
        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
                <Package className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Voyages</p>
                <p className="text-2xl font-semibold">{packagesCount}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Clients</p>
                <p className="text-2xl font-semibold">{clientsCount}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
                <Building className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Partenaires</p>
                <p className="text-2xl font-semibold">{businessesCount}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Derniers voyages */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Derniers voyages</h2>
              <Link to="/admin/packages" className="text-blue-600 hover:text-blue-500">
                Voir tous
              </Link>
            </div>
          </div>
          
          <div className="divide-y divide-gray-200">
            {packagesData?.agencyPackages?.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                Aucun voyage trouvé. <Link to="/admin/packages/new" className="text-blue-600 hover:underline">Créer un voyage</Link>
              </div>
            ) : (
              packagesData?.agencyPackages?.slice(0, 5).map((pkg: Package) => (
                <div key={pkg.id} className="p-6">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img 
                        src={pkg.image ? getImageUrl(pkg.image) : 'https://via.placeholder.com/150'} 
                        alt={pkg.title}
                        className="w-12 h-12 object-cover rounded-md mr-4"
                      />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{pkg.title}</h3>
                        <p className="text-sm text-gray-500">{pkg.category}</p>
                      </div>
                    </div>
                    <Link
                      to={`/admin/packages/${pkg.id}`}
                      className="text-blue-600 hover:text-blue-500"
                    >
                      Modifier
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}